import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { isValidEmail } from "@jugl-web/utils/validators";
import { apiErrorHasCode, useRestApiProvider } from "@jugl-web/rest-api";
import { CX_BUSINESS_NAME_KEY, getStorageItem } from "@jugl-web/utils/storage";
import {
  MAX_NAME_LENGTH,
  MAX_WORKSPACE_NAME_LENGTH,
} from "@web-src/modules/auth/conts";
import {
  Button,
  TextInput,
  PageLoaderFull,
  CountryPicker,
  CountryPickerValue,
} from "@jugl-web/ui-kit";
import { EntityLimitReachedAlert } from "@web-src/modules/common/components/EntityLimitReachedAlert";
import { useDefaultCountry } from "../../hooks/useDefaultCountry";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

interface CXWorkspaceFormProps {
  onSuccess: (entityId: string) => void;
  showProfileFields?: boolean;
  initialEmail?: string;
}

export const CXWorkspaceForm: React.FC<CXWorkspaceFormProps> = ({
  onSuccess,
  showProfileFields,
  initialEmail,
}) => {
  const { t } = useTranslations();
  const { entitiesApi, usersApi } = useRestApiProvider();

  const [createProfile] = usersApi.useCreateProfileMutation();
  const [createEntity] = entitiesApi.useCreateEntityMutation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceCountry, setWorkspaceCountry] =
    useState<CountryPickerValue>();
  const [workspaceEmail, setWorkspaceEmail] = useState("");
  const [isWorkspaceSubmitted, setIsWorkspaceSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLimitAlertOpen, setIsLimitAlertOpen] = useState(false);

  // Pre-fill workspace email
  useEffect(() => {
    if (initialEmail) {
      setWorkspaceEmail(initialEmail);
    }
  }, [initialEmail]);

  const defaultCountry = useDefaultCountry();

  // Pre-fill country from API response
  useEffect(() => {
    if (defaultCountry && !workspaceCountry) {
      setWorkspaceCountry(defaultCountry);
    }
  }, [defaultCountry, workspaceCountry]);

  // Auto-update workspace name
  useEffect(() => {
    const storedBusinessName = getStorageItem<string>(CX_BUSINESS_NAME_KEY);
    if (storedBusinessName) {
      setWorkspaceName(storedBusinessName);
    } else if (firstName) {
      setWorkspaceName(`${firstName}'s Workspace`);
    }
  }, [firstName]);

  const workspaceEmailError = useMemo(() => {
    if (!isWorkspaceSubmitted) return undefined;
    if (!workspaceEmail.length) {
      return t({
        id: "form-controls.email.error-is-required",
        defaultMessage: "Email is empty",
      });
    }
    if (!isValidEmail(workspaceEmail)) {
      return t({
        id: "form-controls.email.error-is-incorrect",
        defaultMessage: "Email is incorrect",
      });
    }
    return undefined;
  }, [isWorkspaceSubmitted, workspaceEmail, t]);

  const allWorkspaceFieldsHaveValues =
    (!showProfileFields || (!!firstName.trim() && !!lastName.trim())) &&
    !!workspaceName.trim() &&
    !!workspaceCountry?.iso &&
    !!workspaceEmail;

  const handleWorkspaceSubmit = useCallback(async () => {
    setIsWorkspaceSubmitted(true);
    if (
      (showProfileFields && (!firstName.trim() || !lastName.trim())) ||
      !workspaceName.trim() ||
      !workspaceCountry?.iso ||
      !isValidEmail(workspaceEmail)
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const [, entityResult] = await Promise.all([
        showProfileFields
          ? createProfile({
              first_name: firstName.trim(),
              last_name: lastName.trim(),
            }).unwrap()
          : Promise.resolve(null),
        createEntity({
          type: "cx",
          data: {
            name: workspaceName.trim(),
            country: workspaceCountry.iso,
            billing_email: workspaceEmail ?? undefined,
          },
          meta: {
            onError: (error) => {
              if (apiErrorHasCode(error, "max_limit_reached")) {
                setIsLimitAlertOpen(true);
                return undefined;
              }
              return "unhandled";
            },
          },
        }).unwrap(),
      ]);

      onSuccess(entityResult.id);
    } catch {
      // Error toast is handled globally by the API result pipeline
    } finally {
      setIsLoading(false);
    }
  }, [
    createEntity,
    createProfile,
    firstName,
    lastName,
    onSuccess,
    showProfileFields,
    workspaceCountry?.iso,
    workspaceEmail,
    workspaceName,
  ]);

  return (
    <>
      <PageLoaderFull isActive={isLoading} isTransparent />
      <EntityLimitReachedAlert
        isOpen={isLimitAlertOpen}
        onClose={() => setIsLimitAlertOpen(false)}
      />
      <div className="my-auto flex w-full max-w-[500px] flex-col items-center justify-center py-2 sm:py-4">
        <span className="text-center font-secondary text-[28px] leading-9 font-medium text-dark sm:text-[32px] sm:leading-[48px]">
          {t({
            id: "cx-ai-agents-page.lets-start-with-essentials",
            defaultMessage: "Let's start with essentials",
          })}
        </span>
        <span className="mt-2 text-center font-secondary text-sm leading-4 text-grey-900">
          {t({
            id: "cx-ai-agents-page.just-a-few-details",
            defaultMessage: "Just a few details to start with",
          })}
        </span>
        <div className="mt-6 flex w-full flex-col gap-6 sm:mt-10">
          {showProfileFields && (
            <div className="flex w-full flex-col gap-4 sm:flex-row">
              <TextInput
                placeholder={t({
                  id: "common.enter",
                  defaultMessage: "Enter",
                })}
                label={t({
                  id: "common.first-name",
                  defaultMessage: "First Name",
                })}
                isRequired
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                isInvalid={isWorkspaceSubmitted && !firstName.trim()}
                classNames={{ wrapperClassName: "w-full sm:flex-1" }}
                maxLength={MAX_NAME_LENGTH}
              />
              <TextInput
                placeholder={t({
                  id: "common.enter",
                  defaultMessage: "Enter",
                })}
                label={t({
                  id: "common.last-name",
                  defaultMessage: "Last Name",
                })}
                isRequired
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                isInvalid={isWorkspaceSubmitted && !lastName.trim()}
                classNames={{ wrapperClassName: "w-full sm:flex-1" }}
                maxLength={MAX_NAME_LENGTH}
              />
            </div>
          )}
          <div className="flex w-full flex-col gap-4 sm:flex-row">
            <TextInput
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "create-entity-form.workspace-name",
                defaultMessage: "Workspace Name",
              })}
              isRequired
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              isInvalid={isWorkspaceSubmitted && !workspaceName.trim()}
              classNames={{ wrapperClassName: "w-full sm:flex-1" }}
              maxLength={MAX_WORKSPACE_NAME_LENGTH}
            />
            <div className="w-full min-w-0 sm:flex-1">
              <CountryPicker
                countryIso={workspaceCountry?.iso}
                onSelect={(item) => setWorkspaceCountry(item)}
                placement="top"
                className="z-[9999]"
              >
                {() => (
                  <TextInput
                    value={
                      workspaceCountry?.name
                        ? `${workspaceCountry.name} (${workspaceCountry.iso})`
                        : ""
                    }
                    isRequired
                    label={t({
                      id: "create-entity-form.workspace-country",
                      defaultMessage: "Workspace Country",
                    })}
                    placeholder={t({
                      id: "common.select",
                      defaultMessage: "Select",
                    })}
                    classNames={{
                      wrapperClassName: "w-full",
                      inputClassName: "cursor-pointer",
                    }}
                    isInvalid={isWorkspaceSubmitted && !workspaceCountry?.iso}
                    readOnly
                  />
                )}
              </CountryPicker>
            </div>
          </div>
          <TextInput
            placeholder="email@gmail.com"
            label={t({
              id: "create-entity-form.workspace-email",
              defaultMessage: "Workspace Email",
            })}
            isRequired
            value={workspaceEmail}
            onChange={(e) => setWorkspaceEmail(e.target.value)}
            isInvalid={isWorkspaceSubmitted && !!workspaceEmailError}
            errorMessage={workspaceEmailError}
            type="email"
          />
        </div>
        <Button
          className="mt-8 w-full sm:mt-10"
          onClick={handleWorkspaceSubmit}
          isDisabled={!allWorkspaceFieldsHaveValues || isLoading}
          isLoading={isLoading}
          iconEnd={<ArrowIcon />}
        >
          {t({
            id: "cx-ai-agents-page.create-workspace",
            defaultMessage: "Create Workspace",
          })}
        </Button>
      </div>
    </>
  );
};
